import { ChevronLeftIcon, Loader2Icon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { RegionForm } from './region-form';
import { Button } from '@/components/ui/button';
import { listRegions, type RegionRecord } from '@/lib/auth-client';
import { toast } from 'sonner';

export function EditRegionPage() {
  const { id } = useParams<{ id: string }>();
  const [region, setRegion] = useState<RegionRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      if (!id) return;
      
      try {
        const { data, error } = await listRegions();
        if (error) throw new Error("API Error");
        
        if (data && data.data) {
          const found = data.data.find((regionItem: RegionRecord) => regionItem.id === id);
          if (found) {
            setRegion(found);
          } else {
            toast.error("Région introuvable");
          }
        }
      } catch (err) {
        console.error(err);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [id]);

  return (
    <section className="mx-auto max-w-4xl space-y-8 pb-20">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="-ml-2 mb-2 text-muted-foreground hover:text-foreground"
          >
            <Link to="/admin/regions">
              <ChevronLeftIcon className="mr-1 size-4" />
              Retour à la liste
            </Link>
          </Button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Modifier la région</h1>
            {region && (
              <span className={`inline-flex items-center justify-center rounded-full border px-2.5 py-1 text-xs font-medium uppercase ${
                region.status === 'Active' ? 'border-green-200 bg-green-50 text-green-700' : 
                region.status === 'Terminé' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-slate-50 text-slate-700'
              }`}>
                {region.status}
              </span>
            )}
          </div>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            {region?.name} — {region?.code}
          </p>
        </div>
      </div>

      <div>
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2Icon className="size-8 animate-spin text-primary" />
          </div>
        ) : region ? (
          <RegionForm key={region.id} initialData={region} isEdit />
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Région non trouvée.</p>
            <Button asChild variant="link" className="mt-2">
              <Link to="/admin/regions">Retourner à la liste</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
